import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  selectors as willCallSelectors,
  actions as willCallActions,
  OrderLineItem,
  SubOrderLineItem
} from 'services/willcall';
import { selectors as configSelectors, Configuration, LanguageField } from 'services/configuration';
import { actions as kioskActions, selectors as kioskSelectors } from 'services/kiosk';
import {
  Box,
  FlexBox,
  Typography,
  Checkbox,
  Selector,
  Collapse,
  Grid,
  MuiBox,
  MuiTable,
  IconButton,
  Icon
} from '@kis-technologies/kis-ui';
import { Scrollbar } from 'components/Scrollbar';
import { isToday, isPast } from 'date-fns';
import { formatCurrency, formatTitle } from 'utils/stringFormat';
import { Footer } from 'components/Footer';
import { useActions } from 'hooks/useActions';
import { ActionOverlay } from 'components/ActionOverlay';
import { LoadingOverlay } from 'components/LoadingOverlay';
import get from 'lodash.get';
import { useStyles } from './styled';
import { useTranslation } from 'react-i18next';

const OrdersView = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [resetOrderDetails, createOrderTickets, resetTickets] = useActions([
    willCallActions.resetOrderDetails,
    kioskActions.createOrderTickets,
    kioskActions.resetTickets
  ]);

  const tickets = useSelector(kioskSelectors.selectTickets);
  const loading = useSelector(kioskSelectors.selectLoading);
  const error = useSelector(kioskSelectors.selectError);
  const customer = useSelector(willCallSelectors.selectCustomer);
  const ordersList = useSelector(willCallSelectors.selectOrdersList);
  const orderDetails = useSelector(willCallSelectors.selectOrderDetails);
  const config = useSelector(configSelectors.selectConfiguration) as Configuration;

  const languageSettings: LanguageField[] = get(config, 'configuration.languageSettings', []);
  const fontFamily = get(config, 'configuration.globalSettings.fonts.primary');
  const palette = get(config, 'configuration.globalSettings.palette');
  const skipTicketList = get(config, 'willCall.skipTicketList');
  const expandedOrderItems = get(config, 'willCall.expandedOrderItems');
  const selectAllTickets = get(config, 'willCall.selectAllTickets');
  const support = get(config, `ticketing.supportName.${i18n.language}`, t('commonText.support'));
  const cartItemLabel = get(config, `configuration.labels.cartItem.${i18n.language}`, t('cart.cartItemLabel'));

  const emptyTicketMessages = new Map([
    ['today', t('orders.noOrderForToday')],
    ['upcoming', t('orders.noUpcomingOrder')]
  ]);

  type SelectorValue = 'today' | 'upcoming';
  const [ticketsSelected, setTicketsSelected] = useState<number[]>([]);
  const [showActionOverlay, setShowActionOverlay] = useState<boolean>(false);
  const [selectorValue, setSelectorValue] = useState<SelectorValue>('today');
  const [rowMap, setRowMap] = useState<Map<any, any> | null>(null);
  const [formattedRows, setFormattedRows] = useState<OrderLineItem[]>([]);
  const [allTickets, setAllTickets] = useState<SubOrderLineItem[]>([]);
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});

  const classes = useStyles({ fontFamily })();

  React.useEffect(() => {
    if (orderDetails) {
      const rows = new Map();
      rows.set(
        'today',
        orderDetails.filter((order) => isToday(new Date(order.date)))
      );
      rows.set(
        'upcoming',
        orderDetails.filter((order) => !isToday(new Date(order.date)) && !isPast(new Date(order.date)))
      );
      setRowMap(rows);

      const subLineItems: SubOrderLineItem[] = [];
      orderDetails.forEach((orderLine) => {
        if (orderLine.subOrderLineItems) {
          orderLine.subOrderLineItems.forEach((subOrderLine) => subLineItems.push(subOrderLine));
        }
      });
      setAllTickets(subLineItems);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderDetails]);

  React.useEffect(() => {
    if (skipTicketList) {
      setTicketsSelected(
        allTickets
          .filter((item) => !item.isPrinted)
          .map((item) => {
            return item.sublineItemId;
          })
      );
    } else if (selectAllTickets) {
      // Handle auto selection of all tickets for the day
      const todayItems = orderDetails.filter((order) => isToday(new Date(order.date)));
      const isSelectAllChecked = selectAllChecked(todayItems);
      if (!isSelectAllChecked) {
        onSelectAll(todayItems);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allTickets]);

  React.useEffect(() => {
    if (rowMap && selectorValue) {
      setFormattedRows(rowMap.get(selectorValue));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowMap, selectorValue]);

  React.useEffect(() => {
    if (formattedRows) {
      if (expandedOrderItems) {
        const tempRows: Record<number, boolean> = [];
        formattedRows.forEach((item, idx) => {
          if (item.isPackage || (!item.isPackage && !item.isGA)) tempRows[idx] = true;
        });
        setExpandedRows(tempRows);
      } else {
        setExpandedRows({});
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formattedRows]);

  React.useEffect(() => {
    if (skipTicketList && ticketsSelected && ticketsSelected.length) {
      onPrintClick();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketsSelected]);

  React.useEffect(() => {
    if (tickets.length) {
      navigate('/willcall/delivery');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets]);

  const handleTryAgain = () => {
    if (skipTicketList) {
      onPreviousClick();
    } else {
      resetTickets();
    }
  };

  const onPrintClick = () => {
    const params = allTickets
      .filter((row) => ticketsSelected.includes(row.sublineItemId))
      .map((row) => row.printParams);

    createOrderTickets(params);
  };

  const onPreviousClick = () => {
    navigate('/willcall/orders-list');
    resetOrderDetails();
  };

  const handleBackClick = () => {
    if (ordersList && ordersList.length) {
      resetOrderDetails();
      navigate('/willcall/orders-list');
    } else {
      setShowActionOverlay(true);
    }
  };

  const toggleTicketSelection = (id: number, isPrinted: boolean) => {
    if (!isPrinted) {
      if (ticketsSelected.includes(id)) {
        setTicketsSelected(ticketsSelected.filter((ticketId) => ticketId !== id));
      } else {
        setTicketsSelected(ticketsSelected.concat(id));
      }
    }
    return true;
  };

  const onChangeSubOrder = (row: SubOrderLineItem) => {
    toggleTicketSelection(row.sublineItemId, row.isPrinted);
  };

  const onChangeOrder = (row: OrderLineItem) => {
    const isRowChecked = rowChecked(row);
    let selectedList = ticketsSelected;
    if (row.subOrderLineItems) {
      row.subOrderLineItems.forEach((item) => {
        if (isRowChecked) {
          selectedList = selectedList.filter((ticketId) => ticketId !== item.sublineItemId);
        } else {
          selectedList = selectedList.concat(item.sublineItemId);
        }
      });
      setTicketsSelected(selectedList);
    }
  };

  const onSelectAll = (orderLineItems: OrderLineItem[]) => {
    const isSelectAllChecked = selectAllChecked(orderLineItems);
    let selectedList: number[] = [...ticketsSelected];
    if (!isSelectAllChecked) {
      orderLineItems.forEach(
        (row) =>
          row.subOrderLineItems &&
          row.subOrderLineItems.forEach((item) => (selectedList = selectedList.concat(item.sublineItemId)))
      );
    } else {
      orderLineItems.forEach(
        (row) =>
          row.subOrderLineItems &&
          row.subOrderLineItems.forEach(
            (item) => (selectedList = selectedList.filter((ticketId) => ticketId !== item.sublineItemId))
          )
      );
    }
    setTicketsSelected(selectedList);
  };

  const selectAllChecked = (orderLineItems: OrderLineItem[]) => {
    let isAllItemChecked = true;
    orderLineItems.every((row) => {
      if (row.subOrderLineItems) {
        row.subOrderLineItems.every((subLineItem) => {
          if (!ticketsSelected.includes(subLineItem.sublineItemId)) {
            isAllItemChecked = false;
            return false;
          }
          return true;
        });
      }
      return isAllItemChecked;
    });
    return isAllItemChecked;
  };

  const isAllPrinted = (): boolean => {
    let isPrinted = false;
    if (formattedRows && formattedRows.length) {
      isPrinted = formattedRows.every((row) => {
        if (row.subOrderLineItems) {
          return row.subOrderLineItems.every((item) => item.isPrinted);
        }
        return false;
      });
    }

    return isPrinted;
  };

  const isRowPrinted = (row: OrderLineItem): boolean => {
    let isPrinted = false;
    if (row.subOrderLineItems) {
      isPrinted = row.subOrderLineItems.every((item) => item.isPrinted);
    }

    return isPrinted;
  };

  const rowChecked = (row: OrderLineItem) => {
    let isAllItemChecked = true;
    if (row.subOrderLineItems) {
      row.subOrderLineItems.every((subLineItem) => {
        if (!ticketsSelected.includes(subLineItem.sublineItemId)) {
          isAllItemChecked = false;
          return false;
        }
        return true;
      });
    }
    return isAllItemChecked;
  };

  const willCallView = () => (
    <Scrollbar refreshModel={expandedRows}>
      <Box flexOverride="none">
        <MuiTable.TableContainer id="cartViewTable">
          <MuiTable.Table className={classes.tableContainer} stickyHeader>
            <MuiTable.TableHead>
              <MuiTable.TableRow className={classes.tableHeadRows}>
                <MuiTable.TableCell
                  className={`${classes.tableHeadCellCheckBox} ${classes.ordersViewCheckboxCell}`}
                  onClick={() => onSelectAll(formattedRows)}
                >
                  <Checkbox
                    size="medium"
                    className={`${classes.ordersViewCheckbox}`}
                    checked={selectAllChecked(formattedRows)}
                    disabled={isAllPrinted()}
                    data-testid="select-all-checkbox"
                    label={
                      <Typography fontSize={22} fontWeight={600} sx={{ marginLeft: '-10px' }}>
                        {t('commonText.selectAll')}
                      </Typography>
                    }
                  />
                </MuiTable.TableCell>
                <MuiTable.TableCell className={`${classes.tableHeadCell} ${classes.cartCollapseCell}`} />
                <MuiTable.TableCell className={`${classes.tableHeadCell} ${classes.cartItemCell}`}>
                  {formatTitle(cartItemLabel)}
                </MuiTable.TableCell>
                <MuiTable.TableCell className={`${classes.tableHeadCell} ${classes.cartTicketCell}`}>
                  {t('cart.type')}
                </MuiTable.TableCell>
                <MuiTable.TableCell
                  className={`${classes.tableHeadCell} ${classes.cartPriceCell} ${classes.cartNumberCell}`}
                >
                  {t('commonText.price')}
                </MuiTable.TableCell>
              </MuiTable.TableRow>
            </MuiTable.TableHead>
            <MuiTable.TableBody>
              {formattedRows.map((row: OrderLineItem, idx: number) => {
                return <Row key={idx} idx={idx} row={row} />;
              })}
            </MuiTable.TableBody>
          </MuiTable.Table>
        </MuiTable.TableContainer>
      </Box>
    </Scrollbar>
  );

  const getRowStyle = (isExpand: boolean, isPrinted: boolean): React.CSSProperties => {
    let expandStyle = {};
    let printedStyle = {};
    if (isExpand) {
      expandStyle = { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 };
    } else {
      expandStyle = { borderRadius: '8px' };
    }
    if (isPrinted) {
      printedStyle = {
        opacity: 0.4,
        boxShadow: '0 3px 9px 0 rgba(0, 0, 0, 0.2)',
        backgroundColor: '#f5f6f6'
      };
    }

    return { ...expandStyle, ...printedStyle };
  };

  const Row = (rowProps: { row: OrderLineItem; idx: number }) => {
    const { row, idx } = rowProps;
    const isExpand = expandedRows[idx];
    const isPrinted = isRowPrinted(row);
    const handleClick = () => {
      if (row.isPackage || (!row.isPackage && !row.isGA)) setExpandedRows({ ...expandedRows, [idx]: !isExpand });
    };

    return (
      <>
        <MuiTable.TableRow className={classes.tableRows} onClick={handleClick} sx={getRowStyle(isExpand, isPrinted)}>
          <MuiTable.TableCell
            className={`${classes.tableBodyCellCheckBox} ${classes.ordersViewCheckboxCell}`}
            onClick={(e: any) => {
              e.stopPropagation();
              onChangeOrder(row);
            }}
          >
            {isPrinted ? (
              <Typography fontSize={24} fontWeight={600}>
                {' '}
                {t('orders.printed')}{' '}
              </Typography>
            ) : (
              <Checkbox data-testid={`checkbox-${idx}`} size="medium" checked={rowChecked(row)} />
            )}
          </MuiTable.TableCell>
          <MuiTable.TableCell className={`${classes.tableBodyCell} ${classes.cartCollapseCell}`}>
            {(row.isPackage || (!row.isPackage && !row.isGA)) && (
              <IconButton
                aria-label="expand row"
                size="small"
                style={{ border: `3px solid ${palette?.primary}`, borderRadius: '50%', color: palette?.primary }}
              >
                {isExpand ? (
                  <Icon name="KeyboardArrowUp" fontSize="large" />
                ) : (
                  <Icon name="KeyboardArrowDown" fontSize="large" />
                )}
              </IconButton>
            )}
          </MuiTable.TableCell>
          <MuiTable.TableCell className={`${classes.tableBodyCell} ${classes.cartItemCell}`}>
            <MuiBox className={classes.cartEventTicketColumn}>
              <Typography fontSize={24} fontWeight={600}>
                {row.name}
              </Typography>
              <Typography fontSize={16}>
                {row.date ? t('{{date, weekdayMonthDay}}', { date: new Date(row.date) }) : ''}
                {row.date ? <> &middot; {t('{{date, time}}', { date: new Date(row.date) })}</> : ''}
                {!row.isGA && row.location && <> &middot; {formatTitle(row.location.toLowerCase())} </>}
              </Typography>
            </MuiBox>
          </MuiTable.TableCell>
          <MuiTable.TableCell className={`${classes.tableBodyCell} ${classes.cartTicketCell}`}>
            <MuiBox className={classes.cartEventTicketColumn}>
              <Typography fontSize={24} fontWeight={600}>
                {' '}
                {row.type}{' '}
              </Typography>
            </MuiBox>
          </MuiTable.TableCell>
          <MuiTable.TableCell className={`${classes.tableBodyCell} ${classes.cartPriceCell} ${classes.cartNumberCell}`}>
            <Typography fontSize={24} fontWeight={600}>
              {formatCurrency(row.subTotal as number, {}, languageSettings)}
            </Typography>
          </MuiTable.TableCell>
        </MuiTable.TableRow>

        <MuiTable.TableRow
          className={classes.tableSubRows}
          sx={isExpand ? { border: '3px solid rgba(224, 224, 224, 1)' } : { display: 'none', border: 'none' }}
        >
          <MuiTable.TableCell sx={{ padding: '0px', flex: '1', borderRadius: '8px' }}>
            <Collapse in={isExpand} timeout="auto" unmountOnExit>
              {isExpand &&
                row.subOrderLineItems &&
                row.subOrderLineItems.length > 0 &&
                row.subOrderLineItems.map((subOrderLine: SubOrderLineItem, rowIdx: number) => {
                  return (
                    <MuiTable.Table size="small" aria-label="cart-sub-table" className={classes.subTable} key={rowIdx}>
                      <MuiTable.TableBody>
                        <MuiTable.TableRow sx={{ display: 'flex' }}>
                          <MuiTable.TableCell
                            className={`${classes.tableBodyCellCheckBox} ${classes.ordersViewCheckboxCell}`}
                            onClick={() => onChangeSubOrder(subOrderLine)}
                          >
                            {subOrderLine.name && !row.useCompositeTickets && (
                              <Checkbox
                                size="medium"
                                disabled={subOrderLine.isPrinted}
                                checked={ticketsSelected.includes(subOrderLine.sublineItemId)}
                                onChange={() => onChangeSubOrder(subOrderLine)}
                              />
                            )}
                          </MuiTable.TableCell>
                          <MuiTable.TableCell className={`${classes.tableBodyCell} ${classes.cartCollapseCell}`} />
                          <MuiTable.TableCell className={`${classes.tableBodyCell} ${classes.cartItemCell}`}>
                            <Typography fontSize={24} fontWeight={600}>
                              {subOrderLine.name}
                            </Typography>
                          </MuiTable.TableCell>
                          <MuiTable.TableCell className={`${classes.tableBodyCell} ${classes.cartTicketCell}`}>
                            {!subOrderLine.isGA && subOrderLine.seatInfo && (
                              <FlexBox direction="column" align="start">
                                <Typography fontSize={18} fontWeight={400}>
                                  {t('commonText.section')} {subOrderLine.seatInfo[0].section} &middot;{' '}
                                  {t('commonText.row')} {subOrderLine.seatInfo[0].row} &middot; {t('commonText.seat')}{' '}
                                  {subOrderLine.seatInfo[0].seat}
                                </Typography>
                              </FlexBox>
                            )}
                          </MuiTable.TableCell>
                          <MuiTable.TableCell
                            className={`${classes.tableBodyCell} ${classes.cartPriceSubCell} ${classes.cartNumberCell}`}
                          />
                        </MuiTable.TableRow>
                      </MuiTable.TableBody>
                    </MuiTable.Table>
                  );
                })}
            </Collapse>
          </MuiTable.TableCell>
        </MuiTable.TableRow>
      </>
    );
  };

  return (
    <>
      {!skipTicketList && (
        <>
          <Grid className={classes.ordersViewOuterContainer}>
            <Grid className={classes.ordersViewInnerContainer}>
              <FlexBox fullWidth flexOverride="none" margin="0 0 6" direction="row">
                <FlexBox direction="column" width={800}>
                  {ordersList && ordersList.length ? (
                    <Typography variant="h2">{t('orders.ticketInformation')}</Typography>
                  ) : (
                    <Typography variant="h2">{`${t('commonText.hello')} ${customer.name}${t(
                      'commonText.exclamation'
                    )}`}</Typography>
                  )}
                  <MuiBox className={classes.ordersViewSubHeading}>
                    <Typography fontSize={30}>{t('orders.selectTickets')}</Typography>
                  </MuiBox>
                </FlexBox>
                <FlexBox justify="center" direction="column" align="start">
                  <Selector
                    value={selectorValue}
                    size="medium"
                    onChange={(e, v) => setSelectorValue(v as SelectorValue)}
                  >
                    <Selector.Item data-testid="order-selector-today" label={t('orders.today')} value="today" />
                    <Selector.Item
                      data-testid="order-selector-upcoming"
                      label={t('orders.upcoming')}
                      value="upcoming"
                    />
                  </Selector>
                </FlexBox>
              </FlexBox>
              {!rowMap || rowMap.get(selectorValue).length === 0 ? (
                <FlexBox justify="center" align="center" height={350} flexOverride="none">
                  <Typography variant="h3">{emptyTicketMessages.get(selectorValue)}</Typography>
                </FlexBox>
              ) : (
                willCallView()
              )}
            </Grid>
          </Grid>
          <Footer
            onPrevClick={handleBackClick}
            onNextClick={onPrintClick}
            prevText={t('commonText.back')}
            nextText={t('commonText.printTickets')}
            isNextDisabled={ticketsSelected.length === 0}
            skipNavPrompt={Boolean(showActionOverlay || error)}
          />
        </>
      )}
      {showActionOverlay && (
        <ActionOverlay
          title={t('commonText.goBackTitle')}
          height={450}
          cancelText={t('commonText.goBack')}
          confirmText={t('commonText.cancel')}
          onCancel={onPreviousClick}
          onConfirm={() => setShowActionOverlay(false)}
        />
      )}
      {error && (
        <ActionOverlay
          title={
            <Box margin="0 0 5" width={910}>
              <Typography fontSize={64} fontWeight={600} align="center" sx={{ lineHeight: 1 }}>
                {t('orders.problemPrinting')}
              </Typography>
            </Box>
          }
          description={
            <Box width={710}>
              <Typography fontSize={30} align="center">
                {t('orders.problemDescription')} {support}
              </Typography>
            </Box>
          }
          confirmText={t('commonText.tryAgain')}
          onConfirm={handleTryAgain}
        />
      )}
      {loading && <LoadingOverlay message={t('orders.hangTight')} />}
    </>
  );
};

export default OrdersView;
